import { call, put, takeEvery, takeLatest, select, debounce } from 'redux-saga/effects';
import { find } from 'lodash';
import { getLocation, matchPath } from 'connected-react-router';
import { constants as timeplanConstants } from '../timeplans/redux';
import { getEvents } from '../timeplans/selector';
import { actions, constants } from './redux';
import { actions as crewingStatusesActions } from '../crewing-statuses/redux';
import selector from './selector';
import Api from '../../utils/Api';

export function* fetchItems() {
  try {
    const featureState = yield select(selector);
    const fields = { users: 'crewing_applicant_id,name,surname,middle,status,email,birthday,address,mobile_phone,phonehome,readiness_date,avatar,last_expirience,role,hot,valid_states,inpool_owner' };
    const { data } = yield call(Api.users.index, { ...featureState, fields });

    yield put(actions.addItems(data));
  } catch (e) {
    yield put(actions.loadItemsError(e));

    console.error(e);
  }
}

export function* fetchItemsOnOpenSection({ payload: { field, value } }) {
  const { didLoad } = yield select(selector);

  if (field === 'isSelectNewUserMode' && value) {
    // Pre-Epic-13f this read `s.form.timeplans.values` — the
    // redux-form slice. After the RHF migration redux-form is gone
    // (state.form is undefined), so the old `s.form.timeplans.values`
    // access throws `Cannot read properties of undefined (reading
    // 'timeplans')` and tears the saga down. QA bug 2.3 (2026-05-19)
    // reproduced this by clicking "Add user" inside a timeplan slot
    // vacancy — the saga's CHANGE_STATE handler fires immediately and
    // the timeplan-modal UI doesn't recover.
    //
    // Read the originRoleId / originRoleName from the same place the
    // RHF form's selector does: walk the events list (which already
    // joins `state.timeplans.data` with `manningScaleById` to attach
    // role info per slot) and pluck the slot we're currently editing
    // out by the id in the URL.
    const { pathname } = yield select(getLocation);
    const editMatch = matchPath(pathname, { path: '/(timeplans|vacancies)/:id/edit' });
    const editId    = editMatch?.params?.id;
    const events    = yield select(getEvents);
    const slot      = editId ? find(events, { id: editId }) : null;
    const originRoleId   = slot?.originRoleId   ?? null;
    const originRoleName = slot?.originRoleName ?? null;

    yield put(actions.changeFilter('role', { value: originRoleId, label: originRoleName }));
    yield put(actions.changeSearch(''));
    yield put(actions.loadItemsRequest());
  }

  if (field === 'isUserSectionOpen' && value && !didLoad) {
    yield put(actions.loadItemsRequest());
  }
}

export function* fetchItemsOnSetIsOpenState({ payload: { state } }) {
  if (state && state.isUserSectionOpen) {
    yield put(actions.loadItemsRequest());
  }
}

export function* fetchAfterSearch() {
  yield put(actions.applySearch()); // reset current pagination page

  // Through the list watcher, so a page still loading is cancelled (CREW-1268).
  yield put(actions.loadItemsRequest());
}

function* loadFilterDeps() {
  const { didLoad } = yield select((s) => s.crewingStatuses);

  if (!didLoad) yield put(crewingStatusesActions.loadItemsRequest());
}
/**
 * getFeatureSagas (rename)
 * each saga file should return all feature sagas
 * @returns {ForkEffect[]}
 */
export default function getFeatureSagas() {
  return [
    // takeLatest (CREW-1268): a newer request cancels the one in flight, so an
    // older answer arriving last cannot overwrite the list.
    takeLatest([
      constants.APPLY_FILTER,
      constants.CHANGE_PAGE_NUMBER,
      constants.CHANGE_PAGE_ITEMS_COUNT,
      constants.LOAD_ITEMS_REQUEST,
    ], fetchItems),

    takeEvery(timeplanConstants.CHANGE_STATE, fetchItemsOnOpenSection),
    takeEvery(timeplanConstants.UPDATE_STATE_BY_PARAMS, fetchItemsOnSetIsOpenState),
    debounce(700, constants.CHANGE_SEARCH, fetchAfterSearch),
    takeEvery(timeplanConstants.ITEMS_LOADED_SUCCESSFULLY, loadFilterDeps),
  ];
}
